/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useMemo, useRef, useState} from 'react'
import {Spinner} from "react-bootstrap";
import {HorizontalTabs} from '../../../../components/Tabs/HorizontalTabs';
import {useSearchParams} from 'react-router-dom';
import RadarPerformance from './RadarPerformance/RadarPerformance';
import RadarDescription from './RadarDescription/SensorDescription';
import {loginRequest, requestIdToken} from "../../../../../auth/authConfig";
import {useMsal} from '@azure/msal-react';
import {theme} from '../../../../../constants/theme';
import WithWizardProvider from "../../../../components/Wizard/WithWizardProvider";
import {radarSlice} from "../../../../../redux/ducks/Wizard/slices";
import {useAppSelector} from "../../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../../redux/ducks/GlobalDialog/slice";
import CloseGlobalDialogButton from "../../../../components/Global/Dialog/CloseGlobalDialogButton";
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import toast from "react-hot-toast";
import queryString from "query-string";
import {StringParam, useQueryParam} from "use-query-params";
import {FAVORITE_PARAM_ID, INNERVTAB_URL_PARAM, MAP_OBJECT_PROFILE_NAME_TO_KEY} from "./constants";
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";

import {Box, CircularProgress, IconButton, Toolbar, Tooltip, Typography} from '@mui/material';
import {usePostRequest} from "../../../../../api/helper";
import {addRadarConfigEndpoint, deleteRadarConfigEndpoint, deleteRadarTempFileEndpoint, errorMessagesTitle, fileProcessingMessagesTitle, getRadarDetailsEndpoint, uploadFileRadarEndpoint, sensorRadarTitle} from '../constants';
import {config} from "../../../../../AppConfig/config";
import NoteAddOutlined from "@mui/icons-material/AutoFixHighOutlined";
import {useDispatch} from "react-redux";
import {useSelectWizardOpenc, useSliceActions} from "../../../../../redux/ducks/Wizard/SliceProvider";
import {WizardDialog} from "../../../../components/Wizard/WizardDialog";
import StepUploadFile from "../../../../components/Wizard/Steps/StepUploadFile";
import {InstructionsStep} from "../../../../components/Wizard/Steps/InstructionsStep";
import {StepPlot} from "../Wizard/Steps/StepPlot";
import styled from "styled-components";
import ArgosFlexbox from '../../../../components/Containers/ArgosFlexbox';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {ETrackedItemTypes} from "../../../../../constants/trackedItems";
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../../hooks/useSetRecentlyViewedWithDeps";
import AdasUsage from "../../../../components/AdasUsageTab/AdasUsage";
import {groupObjectsByTypeNameVersionProfile} from "./RadarPerformance/CollapsibleTabs/ObjectProperties";
import {cloneDeep} from "lodash";
import SensorsEmptyText from '../../../../components/Text/SensorsEmptyText';
import { handleError } from '../../../camera/SensorCamera/ItemDetails/CameraItemDetails';

const BASELINE_CONFIG_ID = 'baseline'

const Container = styled.div({
  display: 'flex',
  flexFlow: 'column',
  overflow: 'auto',
  height: '100%'
})

const ItemDetails = () => {
  const [selectedConfigId, setSelectedConfigId] = useQueryParam(FAVORITE_PARAM_ID, StringParam)
  const hasWriteAccess = useHasWriteAccess()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tabs, setTabs] = useState<any>()

  const {instance, accounts} = useMsal();

  /**
   * This block of code is for getting the parameters from either the url or the dialog.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = Object.fromEntries(searchParams)
  const params: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (dialogState.isOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const id = params?.id

  const [pageTitle, setPageTitle] = useState("");
  const refTitle = useRef<any>("")
  const gmid = useAppSelector((state: any) => selectCurrentUser(state).id)


  // Parsing and Source File
  const dispatch = useDispatch()
  const uploadFileUrl = `${config.url.API_URL}${uploadFileRadarEndpoint}`
  const {setWizardOpen, setWizardMetaData} = useSliceActions()
  const isWizardOpen = useSelectWizardOpenc()
  const [sensorData, setSensorData] = useState<any>({})
  const [isNewSensor, setIsNewSensor] = useState<any>(true);
  const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false)

  useEffect(() => {
    if (isWizardOpen) return
    fetchBaselineData()
    // Leaving here until we can verify blob storage ttl rule works
    // checkAndDeleteOrphanFiles()
  }, [isWizardOpen])

  // Leaving here until we can verify blob storage ttl rule works
  const deleteTempFileHandle = usePostRequest(deleteRadarTempFileEndpoint)
  const checkAndDeleteOrphanFiles = () => {
    const _id = sensorData.id
    const supplier = sensorData.supplier?.replace("/", "_")
    const component = sensorData.component?.replace("/", "_")
    const version = sensorData.version
    const baseFileName = `${supplier}_${component}_${version}_${_id}`
    const tempFileName = `Temp_${baseFileName}.json`
    const sourceFileName = `${baseFileName}.xlsx`
    let reqData: any = {
      "tempFileInfo": {
        "fileName": tempFileName,
        "reqType": "radar"
      },
      "sourceFileInfo": {
        "fileName": sourceFileName,
        "reqType": "radar"
      }
    }

    deleteTempFileHandle(reqData)
      .then(_response => {
      })
      .catch(error => {
        toast.error("Error deleting file")
      })
  }

  const handleClickOpen = () => {
    dispatch(setWizardOpen(true))
  };

  const processData = (jsonData: any) => {
    const supplier = jsonData.sensor.supplier.replace("/", "_")
    const component = jsonData.sensor.component.replace("/", "_")
    const version = jsonData.sensor.version
    const id = jsonData.sensor.id
    let sensorPerformanceContent;
    let sensorDescriptionContent;

    const sensorData = {supplier, component, version, id}
    setSensorData({supplier, component, version, id})
    let wizardMetaData = {...sensorData, "wizardType": "Radar"}
    dispatch(setWizardMetaData(wizardMetaData))
    if (Object.keys(jsonData.sensor.plotData).length == 0) {
      sensorPerformanceContent = <SensorsEmptyText hasWriteAccess={hasWriteAccess} wizardButton={wizardButton} />;
      sensorDescriptionContent = <SensorsEmptyText hasWriteAccess={hasWriteAccess} wizardButton={wizardButton} />;
    } else {
      setIsNewSensor(false);
      sensorPerformanceContent = <RadarPerformance sensor={jsonData.sensor} objects={jsonData.objects} onSaveConfiguration={onSaveConfiguration}
                                                   onDeleteConfiguration={onDeleteConfiguration} gmid={gmid}/>
      sensorDescriptionContent = <RadarDescription sensor={jsonData.sensor}/>
    }

    const tabs: any[] = []
    const sensorPerformanceTab = {
      "name": "Radar Performance",
      "content": sensorPerformanceContent
    }
    const sensorDescriptionTab = {
      "name": "Radar Description",
      "content": sensorDescriptionContent
    }
    let adasUsageContent;
    adasUsageContent = <AdasUsage adasUsage={jsonData?.sensor?.adas_usage}/>
    const sensorAdasUsageTab = {
      "name": "Adas Usage",
      "content" : adasUsageContent
    }

    tabs.push(sensorPerformanceTab)
    tabs.push(sensorDescriptionTab)
    tabs.push(sensorAdasUsageTab)
    const title = supplier + " " + component + " V" + version
    setPageTitle(title)
    refTitle.current = title
    setTabs(tabs);
  }

  const handleDownloadSourceFile = async () => {
    let url = `${config.url.API_URL}/HttpTriggerDownloadFile?request=getSensorRadarSource`
    const idTok = await requestIdToken(instance, accounts)
    const supplier = sensorData.supplier.replace("/", "_")
    const component = sensorData.component.replace("/", "_")
    const version = sensorData.version
    const id = sensorData.id
    //const fileName = `${supplier}_${component}_${version}_${id}.xlsx`
    const sensorName= supplier + " " + component + " V" + version
    const fileName = `${id}.xlsx`
    setIsDownloadingFile(true);
    const req_body = {
      "fileName": fileName,
      //"supplier": supplier,
      //"component": component,
      //"version": version
    }
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(req_body)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      if (res.status === 500){
        handleError(res, sensorName)
      }
      else {
        res.blob().then((blob) => {
          const fileURL = window.URL.createObjectURL(blob)
          let alink = document.createElement('a')
          alink.href = fileURL;
          alink.download = `${supplier}_${component}_${version}_` + fileName
          alink.click();
        })
      }
      setIsDownloadingFile(false);
    }).catch((e) => {
      console.log(e)
    })

  }

  const wizardButton = <>{hasWriteAccess && <Tooltip title="Upload New Definition Wizard" arrow>
    <IconButton onClick={() => handleClickOpen()} size={'large'}>
      <NoteAddOutlined style={{color: 'black'}}/>
    </IconButton>
  </Tooltip>}
  </>

  const downloadSourceButton = <>{isDownloadingFile ? <CircularProgress color='primary'/> : hasWriteAccess && !isNewSensor && <Tooltip title="Download Source File" arrow>
    <IconButton onClick={handleDownloadSourceFile} size={'large'}>
      <FileDownloadIcon style={{color: 'black'}}/>
    </IconButton>
  </Tooltip>}</>

  const actionButtons = <ArgosFlexbox>
    {wizardButton}
    {downloadSourceButton}
  </ArgosFlexbox>

  const [pageDetailsData, setPageDetailsData] = useState<any>({})
  const groupedObjects = useMemo(() => groupObjectsByTypeNameVersionProfile(pageDetailsData.objects), [pageDetailsData.objects])
  const refGroupedObjects = useRef<any>(groupedObjects)
  useEffect(() => {
    refGroupedObjects.current = groupedObjects
  }, [groupedObjects])

  const baselineHandle = usePostRequest(getRadarDetailsEndpoint)
  const fetchBaselineData = async () => {
    setIsLoading(true)
    baselineHandle({
      "id": id,
      "owner": gmid
    }).then((jsonData) => {
      setPageDetailsData(jsonData)
      processData(jsonData)
      setIsLoading(false)
    }).catch(err => {
      setIsLoading(true)
    })
  }

  const currentUserGmid = useAppSelector(state => selectCurrentUser(state).id)

  const saveHandle = usePostRequest(addRadarConfigEndpoint)
  const onSaveConfiguration = async (configurationIndex: number | null, baselineData: any, configName: string, paramGmid: string) => {
    const clonedBaselineData = cloneDeep(baselineData)
    /**
     * Set the owner. Right now people can only override their own configs, but that could change
     */
    const isOverride = configurationIndex != null
    if (!isOverride) {
      clonedBaselineData["owner"] = currentUserGmid
    } else {
      clonedBaselineData["owner"] = paramGmid
    }

    // TODO: TrackedItemUid is just configId, and it is not actually, tracked, only the sensor is (the whole page, not configurations). So change this to configId, and possibly tracked configIds.
    let trackedItemUid = ''
    if (isOverride) {
      const search = window.location.search
      let o: any = queryString.parse(search)
      trackedItemUid = o[INNERVTAB_URL_PARAM]
    }

    // Deal with object properties
    const objectType = clonedBaselineData.objectCategory
    const objectName = clonedBaselineData.objectName
    const objectVersion = clonedBaselineData.objectVersion
    const objectVariant = MAP_OBJECT_PROFILE_NAME_TO_KEY[clonedBaselineData.objectVariant]
    if (objectType !== 'Baseline') {
      const objectVariantVal = refGroupedObjects.current[objectType][objectName][objectVersion][objectVariant]
      const objectId = objectVariantVal.id
      clonedBaselineData.objectId = objectId

      // Remove unneeded fields
      delete clonedBaselineData.objectCategory
      delete clonedBaselineData.objectName
      delete clonedBaselineData.objectVersion
      delete clonedBaselineData.objectVariant
      delete clonedBaselineData.rcs
    }

    saveHandle({
      "configName": configName,
      "configData": clonedBaselineData,
      "configIndex": configurationIndex,
      "currentUserGmid": currentUserGmid,
      isOverride,
      "id": id, // sensor id
      "trackedItemUid": trackedItemUid
    }).then((jsonData: any) => {
      processData(jsonData)
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to add configuration")
    })
  }

  const deleteHandle = usePostRequest(deleteRadarConfigEndpoint)
  const onDeleteConfiguration = async (configurationName: string, gmid: string) => {
    const search = window.location.search
    let o: any = queryString.parse(search)
    const trackedItemUid = o[INNERVTAB_URL_PARAM]

    deleteHandle({
      "sensorId": id,
      "configId": "",  // TODO: Pass configId to delete the config
      "configName": configurationName,
      "owner": gmid,
      trackedItemUid,
    })
      .then((jsonData: any) => {
        processData(jsonData)
        /**
         * Could get the index of deleted item eg pass it in, and then set the id in url to index-1.
         * For now, just set it to 'baseline'. Right now, 'baseline' has no meaning.
         */
        setSelectedConfigId(BASELINE_CONFIG_ID)
      }).catch((err) => {
      console.log(err)
      toast.error("Failed to delete config.")
    });
  }
  const icons = (
    <div style={{display: 'flex', alignItems: 'center'}}>
      <CloseGlobalDialogButton/>
    </div>
  )

  /**
   * Recently viewed
   */
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid: id,
    partitionKey: id,
    trackedItemType: ETrackedItemTypes.RADAR_CONFIGURATION,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)
  useEffect(() => {
    if(pageTitle)
      document.title = sensorRadarTitle + ' - ' + pageTitle;
    return () => {
      document.title = sensorRadarTitle + ' - Landing';
    };
  }, [pageTitle]);
  return (
    <Container>
      <div style={{height: "100%", display: "flex", flexDirection: "column"}}>
        <div>
          <Box sx={{pb: 0}}>
            <div>
              <Toolbar variant="dense" style={{
                alignItems: "center",
                justifyContent: "space-between",
                background: theme.layout.sidebar.backgroundColor
              }}>
                <Typography sx={{color: '#ffffff', fontSize: 14, width: 490, marginTop: 0.5}}>{pageTitle}</Typography>
                {icons}
              </Toolbar>
            </div>
          </Box>
        </div>
        {
          isLoading ?
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
              <Spinner animation={'border'} style={{color: 'grey'}}/>
            </div>
            :
            <div style={{flexGrow: 1, overflow: 'auto'}}>
              <HorizontalTabs initContent={null} initHtabIndex={0} horizontalTabs={tabs} align={'left'}
                              favoriteType={ETrackedItemTypes.RADAR_CONFIGURATION} uid={'htabs-1'} actionButtons={actionButtons}/>
            </div>
        }
      </div>
      <WizardDialog headerTitle={'Import Radar Wizard'}>
        <StepUploadFile uploadFileUrl={uploadFileUrl} messagesTitle={fileProcessingMessagesTitle}/>
        <InstructionsStep indexForErrorMessages={0} indexForWarningMessages={0} type={'general_info'} messagesTitle={errorMessagesTitle}/>
        <InstructionsStep indexForErrorMessages={1} indexForWarningMessages={1} type={'performance'} messagesTitle={errorMessagesTitle}/>
        <StepPlot isLastWizardStep={false} is3d={false} messagesTitle={errorMessagesTitle}/>
        <StepPlot isLastWizardStep={true} is3d={false} messagesTitle={errorMessagesTitle} id={sensorData?.id}/>
      </WizardDialog>
    </Container>

  )
}

export default WithWizardProvider({slice: radarSlice})(ItemDetails)
